import * as React from 'react'
import { tv, VariantProps } from 'tailwind-variants'
import { Switch } from '../Form/Switch'

const stepsSendDocumentsVariants = tv({
  base: 'flex w-full items-center justify-between rounded-lg p-6',
  variants: {
    size: {
      default: 'gap-6',
      lg: 'gap-8'
    }
  },
  defaultVariants: {
    size: 'default'
  }
})

const stepVariants = tv({
  base: 'flex items-center gap-3',
  variants: {
    active: {
      true: 'text-black',
      false: 'text-gray-400'
    }
  },
  defaultVariants: {
    active: false
  }
})

const stepCircleVariants = tv({
  base: 'flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold',
  variants: {
    active: {
      true: 'bg-gray-200 text-black',
      false: 'bg-gray-50 text-gray-200'
    }
  },
  defaultVariants: {
    active: false
  }
})

export interface StepsSendDocumentsProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof stepsSendDocumentsVariants> {
  currentStep?: number
  onToggleAutomaticReading?: (checked: boolean) => void | undefined
  automaticReadingEnabled?: boolean
  stepThreeTitle?: string
  customSteps?: Array<{ number: number; text: string }>
}

const StepsSendDocuments = React.forwardRef<HTMLDivElement, StepsSendDocumentsProps>(
  (
    {
      className,
      size = 'default',
      currentStep = 1,
      onToggleAutomaticReading,
      automaticReadingEnabled = false,
      stepThreeTitle = 'Autorização da ANVISA',
      customSteps,
      ...props
    },
    ref
  ) => {
    const steps = customSteps || [
      { number: 1, text: 'Envio de receita' },
      { number: 2, text: 'Envio de documentos' },
      { number: 3, text: stepThreeTitle },
      { number: 4, text: 'Dados da entrega' }
    ]

    const currentStepData = steps.find((step) => step.number === currentStep)

    return (
      <div ref={ref} className={stepsSendDocumentsVariants({ size, className })} {...props}>
        <div className="flex w-full flex-col gap-4 sm:hidden">
          {currentStepData && (
            <div className="flex items-center gap-3">
              <div className={stepCircleVariants({ active: true })}>{currentStepData.number}</div>
              <span className="text-sm font-medium text-black">{currentStepData.text}</span>
            </div>
          )}

          {onToggleAutomaticReading && (
            <div className="ml-5 flex items-center gap-3">
              <Switch
                checked={automaticReadingEnabled}
                onCheckedChange={onToggleAutomaticReading}
                size="lg"
                className="data-[state=checked]:bg-green-500 data-[state=unchecked]:bg-gray-300"
              />
              <span className="text-sm font-medium text-black">Aceito leitura automática de receita</span>
            </div>
          )}
        </div>

        <div className="hidden w-full items-center justify-between sm:flex">
          <div className="flex items-center gap-6">
            {steps.map((step) => (
              <div key={step.number} className={stepVariants({ active: step.number <= currentStep })}>
                <div className={stepCircleVariants({ active: step.number <= currentStep })}>{step.number}</div>
                <span className="whitespace-nowrap text-sm font-medium">{step.text}</span>
              </div>
            ))}
          </div>

          {onToggleAutomaticReading && (
            <div className="ml-5 flex items-center gap-5">
              <Switch
                checked={automaticReadingEnabled}
                onCheckedChange={onToggleAutomaticReading}
                size="lg"
                className="data-[state=checked]:bg-green-500 data-[state=unchecked]:bg-gray-300"
              />
              <span className="whitespace-nowrap text-sm font-medium text-black">
                Aceito leitura automática de receita
              </span>
            </div>
          )}
        </div>
      </div>
    )
  }
)

StepsSendDocuments.displayName = 'StepsSendDocuments'

export { StepsSendDocuments, stepsSendDocumentsVariants }
